import { Capacitor, registerPlugin } from "@capacitor/core";
import { removeUserPushToken, upsertUserPushToken } from "./db";
import i18n from "@/i18n";

const PushNotifications = registerPlugin("PushNotifications");
const App = registerPlugin("App");

let listenersAttached = false;
let resumeHooksAttached = false;
let currentUid = null;
let lastToken = null;
let lastRegisterTs = 0;

function canUsePushNotifications() {
  return (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable("PushNotifications")
  );
}

async function detachTokenForCurrentUser() {
  if (!currentUid || !lastToken) return;
  try {
    await removeUserPushToken({ uid: currentUid, token: lastToken });
  } catch (error) {
    console.error("No se pudo borrar token push anterior:", error);
  }
}

async function attachListeners() {
  if (listenersAttached) return;

  await PushNotifications.addListener("registration", async (tokenInfo) => {
    const token = tokenInfo?.value;
    if (!token || !currentUid) return;

    lastToken = token;
    try {
      await upsertUserPushToken({
        uid: currentUid,
        token,
        platform: Capacitor.getPlatform(),
      });
    } catch (error) {
      console.error("No se pudo guardar token push:", error);
    }
  });

  await PushNotifications.addListener("registrationError", (error) => {
    console.error("Error registrando push notifications:", error);
  });

  listenersAttached = true;
}

async function registerForPushIfNeeded({ force = false } = {}) {
  if (!canUsePushNotifications() || !currentUid) return;

  const now = Date.now();
  if (!force && now - lastRegisterTs < 10000) return;
  lastRegisterTs = now;

  const permission = await PushNotifications.requestPermissions();
  if (permission?.receive !== "granted") return;

  await attachListeners();
  if (Capacitor.getPlatform() === "android") {
    try {
      await PushNotifications.createChannel({
        id: "event-invites",
        name: i18n.t("profile.notificationLabel"),
        description: i18n.t("profile.notificationLabel"),
        importance: 5,
        visibility: 1,
        sound: "default",
      });
    } catch (_) {}
  }
  await PushNotifications.register();
}

function attachResumeHooks() {
  if (resumeHooksAttached) return;

  window.addEventListener("focus", () => {
    void registerForPushIfNeeded();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void registerForPushIfNeeded();
    }
  });

  if (Capacitor.isPluginAvailable("App")) {
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void registerForPushIfNeeded({ force: true });
      }
    });
  }

  resumeHooksAttached = true;
}

export async function syncPushNotificationsForUser(user) {
  if (!canUsePushNotifications()) return;

  if (!user?.uid) {
    await detachTokenForCurrentUser();
    currentUid = null;
    return;
  }

  if (currentUid && currentUid !== user.uid) {
    await detachTokenForCurrentUser();
    lastToken = null;
  }

  currentUid = user.uid;
  attachResumeHooks();
  await registerForPushIfNeeded({ force: true });
}
